/**
 * The Actions screen's spacing and type, transcribed from the hand-off.
 *
 * WHY THIS FILE EXISTS. Metrics were being corrected one report at a time — a padding here,
 * a font size there — which is slow and never converges, because each fix is a guess at
 * which value was meant. The prototype carries every number as an inline style, so the
 * values are knowable rather than inferable. They are collected here once, and the screen
 * reads them.
 *
 * SOURCES, so any value can be checked rather than trusted:
 *   `~/work/sims/tmp/Actions Pattern v2.dc.html` lines 86-300 — the `onList` branch
 *   `_ds/cmi-design-system-.../colors_and_type.css` lines 42-58 — the type scale
 *   `_ds/cmi-design-system-.../card-system.css` lines 336-353 — `.c-btn`
 *
 * UNITS. The design is in pixels on an 8px grid; MUI's `sx` spacing unit is 8px, so a
 * design `24px` is `3` and `16px` is `2`. Pixel values that are NOT spacing — type sizes,
 * control heights — stay in rem so they follow the user's font settings, which a hard px
 * would not. The comment on each names the design's own value.
 *
 * WHAT IS DELIBERATELY NOT HERE. Colour. Those are palette roles and already bind
 * correctly; see `typeChipStyles.ts` and `sims-themecolors.json`. Mixing them in would
 * re-create the thing that made the JANUS defaults hard to see — one file that looks
 * authoritative for values it does not actually own.
 */

/** The screen's outer rhythm. The design pads every row 24px from the page edge. */
export const PAGE_X = 3; // 24px

/**
 * Row 1 — title block and the controls that act on the whole screen.
 *
 * `alignItems: flex-end` matters and is easy to miss: the title block is three stacked
 * lines and the buttons are one, so aligning them centre floats the buttons against the
 * title's middle. The design bottom-aligns them to the count line.
 */
export const headerRow = {
	display: "flex",
	alignItems: "flex-end",
	justifyContent: "space-between",
	gap: 2, // 16px
	pt: 2.5, // 20px
	pb: 0,
};

/** "Program setup" — the section the screen sits in. */
export const breadcrumb = {
	fontSize: "0.75rem", // 12px
	lineHeight: 1.33,
	color: "text.secondary",
	mb: 0.5, // 4px
};

/**
 * The screen title. 24px/32px at weight 700 — the design system's `--fs-headline-sm` with
 * `.t-headline-sm`, which is `--fw-bold`.
 *
 * MUI's `h4` variant is neither that size nor that weight, which is why the heading read
 * lighter and larger than the frame.
 */
export const screenTitle = {
	fontSize: "1.5rem", // 24px
	lineHeight: "2rem", // 32px
	fontWeight: 700,
	letterSpacing: "-0.019em",
	m: 0,
};

/** The count line under the title. */
export const countLine = {
	fontSize: "0.8125rem", // 13px
	color: "text.secondary",
	mt: "2px",
};

/**
 * Row 2 — the tabs, on their own rule.
 *
 * The hint is bottom-padded 12px so it sits on the tabs' baseline rather than the rule.
 */
export const tabsRow = {
	display: "flex",
	alignItems: "flex-end",
	gap: 3, // 24px
	pt: 1.5, // 12px
	borderBottom: 1,
	borderColor: "divider",
};

export const tabItem = {
	minHeight: "2.75rem", // 44px
	textTransform: "none" as const,
	fontSize: "0.875rem", // 14px
	fontWeight: 600,
	px: 0.25,
};

/**
 * A tab's own label row — the noun and its count pill.
 *
 * The design spaces every item on the tab button 8px apart, glyph included
 * (`Actions Pattern v2.dc.html:121`); MUI already puts 8px between the icon and the label
 * for `iconPosition="start"`, so this is the gap on the other side of the noun. The chip's
 * fill and height are colour, and live in `typeChipStyles.ts` with the rest.
 */
export const tabLabel = {
	display: "inline-flex",
	alignItems: "center",
	gap: 1, // 8px
};

/** The per-tab hint, right-aligned on the tabs row. */
export const tabHint = {
	fontSize: "0.75rem", // 12px
	color: "text.secondary",
	maxWidth: "26.25rem", // 420px
	textAlign: "right" as const,
	pb: 1.5, // 12px
};

/** Row 3 — search, filter chips, result count. */
export const filterRow = {
	display: "flex",
	alignItems: "center",
	gap: 1.5, // 12px
	py: 2, // 16px
	flexWrap: "wrap" as const,
};

/** The result count at the right of the filter row. */
export const resultCount = {
	fontSize: "0.8125rem", // 13px
	color: "text.secondary",
};

/**
 * The gap between the filter row and the table.
 *
 * Zero in the design: the filter row's own 16px bottom padding is the separation, and the
 * card then sits flush. Named rather than left implicit, because "no margin" here is a
 * decision and not an omission — it was reported as a missing gap, and the fix was the
 * filter row's padding, not a margin on the table.
 */
export const tableTop = 0;

/**
 * The icon picker's tile, shared by BOTH editors.
 *
 * It existed twice — 2.5rem with an 8%-alpha fill and a near-black glyph in the action
 * editor, 2.75rem with the tint token and a purple glyph in the group editor. Two
 * definitions of one control, which is how they came to look different on two screens of
 * the same feature. Exactly the drift that the Groups table's copied styles produced, so
 * this lives in one place instead.
 *
 * The glyph is the SIMS purple whether or not it is chosen; selection is carried by the
 * tint fill and the brand border, not by turning the symbol a different colour.
 */
export const iconTile = (selected: boolean) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	width: "2.75rem", // 44px
	height: "2.75rem",
	padding: 0,
	borderRadius: "0.5rem",
	cursor: "pointer",
	border: "1px solid",
	borderColor: selected ? "primary.main" : "divider",
	backgroundColor: selected
		? (t: { palette: { surfaceTint: { main: string } } }) =>
				t.palette.surfaceTint.main
		: "background.paper",
	color: "primary.main",
	"& svg": { fontSize: "1.375rem" },
});

/**
 * The row that pairs Order with the icon grid.
 *
 * `alignItems: flex-start` alone left the two helper lines at different heights, because
 * the Order input is 56px and a tile is 44px — so "Position in the left navigation." sat
 * above "The icon this action shows in the left navigation." The grid rows are given a
 * common minimum instead, and the tiles centre within it.
 */
export const iconRowControl = {
	display: "flex",
	alignItems: "center",
	flexWrap: "wrap" as const,
	gap: 1,
	minHeight: "3.5rem", // the TextField's height, so both helpers start together
};

/**
 * The editors' card, and the two text roles that sit inside it.
 *
 * `CARD` and `CARD_TITLE` were declared identically in both editors — byte for byte, which
 * is the moment to consolidate rather than the moment to relax. The icon tile was identical
 * once too, and by the time anyone noticed it had drifted into two sizes, two fills and two
 * glyph colours.
 *
 * The two text roles are NOT the same thing and were easy to read as one: `fieldLabel` sits
 * ABOVE a control, `fieldHelper` BELOW it. The group editor called the first one FIELD_LABEL
 * and the action editor called the second one HELPER, so the pair looked like a naming
 * inconsistency rather than two roles. Named for their position here.
 */
export const editorCard = {
	backgroundColor: "background.paper",
	border: "1px solid",
	borderColor: "divider",
	borderRadius: "0.75rem",
	padding: "1.25rem 1.5rem 1.5rem",
	display: "flex",
	flexDirection: "column",
	gap: 2.5,
} as const;

export const editorCardTitle = {
	fontSize: "1.25rem",
	fontWeight: 700,
	m: 0,
} as const;

/** The label ABOVE a control. */
export const fieldLabel = {
	fontSize: "0.75rem",
	fontWeight: 500,
	color: "text.secondary",
	mb: 0.75,
} as const;

/** The explanatory line BELOW a control. */
export const fieldHelper = {
	fontSize: "0.75rem",
	color: "text.secondary",
	mt: "0.25rem",
} as const;
